import React, { useEffect, useState } from 'react'
import { VehicleType } from 'shared/store/useCustomers/types'
import { Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import CustomTable from 'shared/components/CustomTable'
import { Delete, Edit } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { sortAlphabetical } from 'shared/utils/helpers/sorters'
import { VehicleModalType } from '../../types'
import { getCustomersVehiclesService } from '../../services'
import Modal from './components/Modal'

interface Props {
   customerUuid?: string
}

export default function Vehicles({ customerUuid }: Props) {
   const [modalDetails, setModalDetails] = useState<VehicleModalType>({
      variant: undefined,
      open: false,
      vehicleDetails: undefined,
   })
   const { t } = useTranslation(['CUSTOMERS', 'TRANSLATION'])
   const [vehicles, setVehicles] = useState<VehicleType[] | undefined>()
   const [error, setError] = useState(false)
   const [searchPhrase, setSearchPhrase] = useState('')

   function handleModalClose() {
      if (customerUuid) {
         getCustomersVehiclesService(customerUuid)
            .then((res) => {
               setVehicles(res.data)
               return res.data
            })
            .catch(() => {
               setError(true)
            })
            .finally(() => {
               setModalDetails({ open: false, vehicleDetails: undefined, variant: undefined })
            })
      }
   }

   function renderContent() {
      if (error) {
         return (
            <Typography variant="h6" color="error">
               {t('CUSTOMERS:vehicleFetchingError')}
            </Typography>
         )
      }
      if (vehicles === undefined) {
         return <CircularProgress />
      }
      return (
         <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', mt: '24px', gap: '12px' }}>
            <TextField
               sx={{ minWidth: '400px' }}
               value={searchPhrase}
               onChange={(e) => setSearchPhrase(e.target.value)}
               label={t('TRANSLATION:search')}
            />
            <CustomTable
               dependencies={[]}
               disablePagination
               dataSize={
                  vehicles.filter((v) =>
                     Object.values(v).some((value) => value?.toLowerCase().includes(searchPhrase.toLowerCase()))
                  ).length
               }
               rows={vehicles
                  .filter((v) =>
                     Object.values(v).some((value) => value?.toLowerCase().includes(searchPhrase.toLowerCase()))
                  )
                  .sort((a, b) => sortAlphabetical(a.registrationNumber, b.registrationNumber))}
               columns={[
                  {
                     name: 'registrationNumber',
                     mainOnMobile: true,
                     renderCell: (row: VehicleType) => row.registrationNumber,
                  },
                  {
                     name: 'vin',
                     mainOnMobile: true,
                     renderCell: (row: VehicleType) => row.vin,
                  },
                  {
                     name: 'brand',

                     renderCell: (row: VehicleType) => row.brand,
                  },
                  {
                     name: 'model',

                     renderCell: (row: VehicleType) => row.model,
                  },
                  {
                     name: 'series',

                     renderCell: (row: VehicleType) => row.serie,
                  },
                  {
                     name: 'carType',

                     renderCell: (row: VehicleType) => t(`CUSTOMERS:modal.vehicleDetails.body.${row.carType}`),
                  },
                  {
                     name: 'engine',

                     renderCell: (row: VehicleType) => row.engine,
                  },
                  {
                     name: 'fuelType',

                     renderCell: (row: VehicleType) => t(`CUSTOMERS:fuelTypes.${row.fuelType}`),
                  },
                  {
                     name: 'totalPower',

                     renderCell: (row: VehicleType) => `${row.totalPower} kW`,
                  },
                  {
                     name: 'engineCapacity',

                     renderCell: (row: VehicleType) => (
                        <>
                           {row.engineCapacity} cm<sup style={{ fontSize: '0.875rem' }}>3</sup>
                        </>
                     ),
                  },
                  {
                     name: 'transmissionType',

                     renderCell: (row: VehicleType) =>
                        t(`CUSTOMERS:modal.vehicleDetails.gearbox.${row.transmissionType?.toLowerCase()}`),
                  },
                  {
                     name: 'productionYear',

                     renderCell: (row: VehicleType) => row.productionYear,
                  },
                  {
                     name: 'description',

                     renderCell: (row: VehicleType) => row.description,
                  },

                  {
                     name: '',
                     mainOnMobile: true,
                     renderCell: (row) => (
                        <Box sx={{ display: 'flex', gap: '4px' }}>
                           <IconButton
                              onClick={() => setModalDetails({ open: true, variant: 'EDIT', vehicleDetails: row })}
                           >
                              <Edit />
                           </IconButton>
                           <IconButton
                              onClick={() => setModalDetails({ open: true, variant: 'DELETE', vehicleDetails: row })}
                              sx={{ '&:hover': { color: 'red' } }}
                           >
                              <Delete />
                           </IconButton>
                        </Box>
                     ),
                  },
               ]}
            />
         </Box>
      )
   }

   useEffect(() => {
      if (customerUuid) {
         getCustomersVehiclesService(customerUuid)
            .then((res) => {
               setVehicles(res.data)
               return res.data
            })
            .catch(() => {
               setError(true)
            })
      }
   }, [customerUuid])

   return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
         <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '16px' }}>
            {renderContent()}
            <OutlinedButton
               onClick={() => setModalDetails({ open: true, variant: 'CREATE', vehicleDetails: undefined })}
               sx={{ m: '0 auto' }}
               variant="outlined"
            >
               {t('CUSTOMERS:addVehicle')}
            </OutlinedButton>
         </Box>

         <Modal customerUuid={customerUuid ?? ''} modalDetails={modalDetails} onClose={handleModalClose} />
      </Box>
   )
}
